import React, { useState, useCallback, useEffect } from 'react';
import { MenuItem, Spinner } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { useDebounce } from "@uidotdev/usehooks";
import type { ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import type { ICellEditorParams } from 'ag-grid-community';

import { getFieldDependentTable } from '../utils/metadata';
import { useGlobalMetadata } from '../utils/providers';
import type { BaseRecord } from '../types/api';
import type { RelationshipLookup } from '../types/metadata';

const SearchRecordSelect = Select.ofType<BaseRecord>();

interface RelatedLookupCellProps extends ICellEditorParams {
  relationship: RelationshipLookup;
  currentDisplayValue?: string;
  schema: string;
  onValueChange?: (value: number | null) => void;
  onCellValueChanged?: (event: {
    data: BaseRecord;
    oldValue: any;
    newValue: any;
    colDef: { field: string };
    node: any;
  }) => void;
}

function RelatedLookupCell(params: RelatedLookupCellProps): JSX.Element {
  const [records, setRecords] = useState<BaseRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<string>(params.currentDisplayValue || '');
  const { relationship } = params;

  // Assertive null check: relationship MUST have valid target_table_name
  if (!relationship.target_table_name) {
    throw new Error(`Invalid relationship in RelatedLookupCell: missing target_table_name`);
  }

  // DIAGNOSTIC: Component lifecycle
  useEffect(() => {
    console.log('[LOOKUP_DEBUG] Component mounted');
    return () => {
      console.log('[LOOKUP_DEBUG] Component unmounted');
    };
  }, []);

  // DIAGNOSTIC: Document-level event listener to test portal theory
  useEffect(() => {
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      console.log('[LOOKUP_DEBUG] Document keydown (capture):', {
        key: event.key,
        target: event.target,
        targetTagName: (event.target as HTMLElement)?.tagName,
        targetClassName: (event.target as HTMLElement)?.className,
        currentTarget: event.currentTarget,
        defaultPrevented: event.defaultPrevented,
        bubbles: event.bubbles,
        eventPhase: event.eventPhase,
      });
    };

    document.addEventListener('keydown', handleDocumentKeyDown, { capture: true });
    console.log('[LOOKUP_DEBUG] Document keydown listener added (capture phase)');

    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown, { capture: true });
      console.log('[LOOKUP_DEBUG] Document keydown listener removed');
    };
  }, []);

  const debouncedQuery = useDebounce(query, 300);

  const { data: metadata } = useGlobalMetadata();

  const tableLabel = metadata?.tableData?.byName?.[relationship.target_table_name]?.ui_label || relationship.target_table_name;

  const fetchRecords = useCallback(async (searchText: string): Promise<void> => {
    if (!searchText) {
      setRecords([]);
      return;
    }
    setLoading(true);

    const conditions: Record<string, any> = {};
    conditions[relationship.target_label_field] = {
      filterType: 'string',
      type: 'contains',
      filter: searchText.toLowerCase(),
    };

    const { data, error } = await getFieldDependentTable({
      tableName: relationship.target_table_name!, // Already validated above
      schema: params.schema,
      conditions,
      orderBy: {
        column: relationship.target_label_field,
        direction: 'ascending',
      },
      limitOffset: {
        limit: 100,
        offset: 0,
      },
      noCache: true,
    });

    if (!error && data) {
      setRecords(Array.isArray(data) ? data : []);
    } else {
      console.error(`Error fetching ${relationship.target_table_name} with conditions:`, conditions);
    }
    setLoading(false);
  }, [relationship.target_label_field, relationship.target_table_name, params.schema]);

  useEffect(() => {
    fetchRecords(debouncedQuery);
  }, [debouncedQuery, fetchRecords]);

  const filterLoadedRecords: ItemPredicate<BaseRecord> = (query: string, record: BaseRecord): boolean => {
    const searchValue = record[relationship.target_label_field];
    if (!searchValue) {
      return false;
    }
    return searchValue.toLowerCase().indexOf(query.toLowerCase()) >= 0;
  };

  const renderRecord: ItemRenderer<BaseRecord> = (record, { handleClick, modifiers }): JSX.Element | null => {
    if (!modifiers.matchesPredicate) {
      return null;
    }
    return (
      <MenuItem
        className="ag-custom-component-popup"
        active={modifiers.active}
        disabled={modifiers.disabled}
        key={record.id}
        onClick={(e) => {
          console.log('[LOOKUP_DEBUG] MenuItem clicked:', {
            record: record[relationship.target_label_field],
            target: e.target,
            currentTarget: e.currentTarget,
            defaultPrevented: e.defaultPrevented,
            isPropagationStopped: e.isPropagationStopped(),
          });
          handleClick(e);
          console.log('[LOOKUP_DEBUG] After handleClick, isPropagationStopped:', e.isPropagationStopped());
        }}
        text={record[relationship.target_label_field] || '< No Name! >'}
        roleStructure="listoption"
      />
    );
  };

  const handleItemSelect = (record: BaseRecord): void => {
    console.log('[LOOKUP_DEBUG] handleItemSelect called:', {
      record: record[relationship.target_label_field],
      recordId: record.id,
    });
    console.log('[LOOKUP_DEBUG] Calling params.stopEditing()');
    params.stopEditing();
    const fieldName = relationship.col_name;

    // Assertive null check: col_name is required for field updates
    if (!fieldName) {
      throw new Error(`Invalid relationship in RelatedLookupCell: missing col_name`);
    }

    const currentData = { ...params.data };
    currentData[fieldName] = record.id;
    currentData[`${fieldName}_rel`] = record;
    params.node.updateData(currentData);
    params.onValueChange?.(record.id);

    // fake the event to match the ag-grid event
    params.onCellValueChanged?.({
      data: params.data,
      oldValue: params.value,
      newValue: record.id,
      colDef: { field: fieldName },
      node: params.node,
    });
  };

  const isInitialSearch = params.currentDisplayValue === query;
  const placeholder = `Search for a ${tableLabel}`;
  let noResultsContents = `No ${relationship.target_table_name}s found`;
  
  if (isInitialSearch) {
    noResultsContents = placeholder;
  }
  if (loading) {
    noResultsContents = 'Loading...';
  }

  return (
    <div
      className="ag-custom-component-popup"
      onKeyDownCapture={(e) => {
        console.log('[LOOKUP_DEBUG] Wrapper onKeyDownCapture fired:', {
          key: e.key,
          target: e.target,
          targetTagName: (e.target as HTMLElement)?.tagName,
          currentTarget: e.currentTarget,
          defaultPrevented: e.defaultPrevented,
          isPropagationStopped: e.isPropagationStopped(),
          eventPhase: e.nativeEvent.eventPhase,
        });

        if (e.key === 'Escape') {
          console.log('[LOOKUP_DEBUG] Escape key detected in wrapper handler');
          e.stopPropagation();
          e.preventDefault();
          console.log('[LOOKUP_DEBUG] Calling params.stopEditing(true) to cancel');
          params.stopEditing(true);
        }
      }}
    >
      <SearchRecordSelect
        resetOnSelect={true}
        items={records}
        itemPredicate={filterLoadedRecords}
        itemRenderer={renderRecord}
        onItemSelect={handleItemSelect}
        onQueryChange={setQuery}
        query={query}
        noResults={
          <MenuItem
            disabled={true}
            text={noResultsContents}
            roleStructure="listoption"
          />
        }
        popoverProps={{
          minimal: true,
          isOpen: true,
          matchTargetWidth: true,
          placement: "bottom",
          className: "ag-custom-component-popup",
          popoverClassName: "ag-custom-component-popup",
          portalClassName: "ag-custom-component-popup"
        }}
        inputProps={{
          className: "ag-custom-component-popup",
          placeholder,
          rightElement: loading ? <Spinner className="ag-custom-component-popup" size={16} /> : undefined,
          autoFocus: true,
          onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select(),
        }}
      />
    </div>
  );
}

export default RelatedLookupCell;